import { useState, useEffect } from "react";

import {
    Star,
    Utensils,
    Package,
    Truck,
    Headphones,
    MoreHorizontal
} from "lucide-react";

import {
    getAdminStatistics
} from "../../../Servicefile/ReportAnalysisService.jsx";

function StarDisplay({ rating }) {

    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(i => (
                <Star
                    key={i}
                    size={16}
                    className={
                        i <= Math.round(rating)
                            ? "fill-orange-400 text-orange-400"
                            : "text-gray-300"
                    }
                />
            ))}
        </div>
    );
}

function FoodRatingsTab({ data: propData }) {

    const [data, setData] = useState(null);

    const [loadingRatings, setLoadingRatings] =
        useState(false);

    const [analyticsData, setAnalyticsData] =
        useState(null);

    /* =========================================
       FORMAT TIME
    ========================================= */

    const formatTime = (dateString) => {

        if (!dateString) return "";

        const diff =
            Date.now() -
            new Date(dateString).getTime();

        const hours =
            Math.floor(
                diff / (1000 * 60 * 60)
            );

        if (hours < 1) return "Just now";

        if (hours < 24)
            return `${hours} hours ago`;

        return `${Math.floor(hours / 24)} days ago`;
    };

    /* =========================================
       FETCH DATA FROM SERVICE
    ========================================= */

    const fetchRatingsAnalytics =
        async () => {

            try {

                setLoadingRatings(true);

                const result =
                    await getAdminStatistics();

                console.log(
                    "Ratings Analytics:",
                    result
                );

                setData(result);

                setAnalyticsData(result);

            } catch (e) {

                console.error(
                    "Ratings API Error:",
                    e
                );

            } finally {

                setLoadingRatings(false);
            }
        };

    /* =========================================
       INITIAL LOAD
    ========================================= */

    useEffect(() => {

        // Parent data available
        if (propData) {

            setData(propData);

            setAnalyticsData(propData);

            return;
        }

        // API call
        fetchRatingsAnalytics();

    }, [propData]);

    /* =========================================
       RATINGS DATA
    ========================================= */

    const ratingDistribution = (
        analyticsData?.ratingDistribution ??
        data?.ratingDistribution
    ) || {
        "5Star": 0,
        "4Star": 0,
        "3Star": 0,
        "2Star": 0,
        "1Star": 0
    };

    const totalRatingsCount =
        Object.values(
            ratingDistribution
        ).reduce(
            (a, b) => a + b,
            0
        );

    const distributionData = [

        {
            star: 5,
            count:
                ratingDistribution["5Star"] || 0,
            color: "bg-green-500"
        },

        {
            star: 4,
            count:
                ratingDistribution["4Star"] || 0,
            color: "bg-green-400"
        },

        {
            star: 3,
            count:
                ratingDistribution["3Star"] || 0,
            color: "bg-yellow-500"
        },

        {
            star: 2,
            count:
                ratingDistribution["2Star"] || 0,
            color: "bg-orange-500"
        },

        {
            star: 1,
            count:
                ratingDistribution["1Star"] || 0,
            color: "bg-red-500"
        }
    ];

    /* =========================================
       CATEGORY RATINGS
    ========================================= */

    const categoryRatings = (
        analyticsData?.categoryRatings ??
        data?.categoryRatings
    ) || {};

    const categoryItems = [

        {
            key: "FOOD_QUALITY",
            label: "Food Quality"
        },

        {
            key: "PACKAGING",
            label: "Packaging"
        },

        {
            key: "DELIVERY",
            label: "Delivery"
        },

        {
            key: "SERVICE",
            label: "Service"
        },

        {
            key: "OTHERS",
            label: "Others"
        }
    ];

    const getCategoryIcon =
        (category) => {

            switch (category) {

                case "FOOD_QUALITY":
                    return (
                        <Utensils
                            size={16}
                            className="text-orange-500"
                        />
                    );

                case "PACKAGING":
                    return (
                        <Package
                            size={16}
                            className="text-orange-500"
                        />
                    );

                case "DELIVERY":
                    return (
                        <Truck
                            size={16}
                            className="text-orange-500"
                        />
                    );

                case "SERVICE":
                    return (
                        <Headphones
                            size={16}
                            className="text-orange-500"
                        />
                    );

                default:
                    return (
                        <MoreHorizontal
                            size={16}
                            className="text-orange-500"
                        />
                    );
            }
        };

    /* =========================================
       METRICS
    ========================================= */
    const avgRating = Number(
        analyticsData?.averageRating ??
        data?.averageRating ??
        0
    );

    const totalRatings = Number(
        analyticsData?.totalRatings ??
        data?.totalRatings ??
        0
    );

    const ratingGrowth = Number(
        analyticsData?.ratingGrowthPercent ??
        data?.ratingGrowthPercent ??
        0
    );

    return (

        <div
            style={{
                height: "72vh",
                overflowY: "auto",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                scrollbarWidth: "thin"
            }}
        >

            {/* TOP METRIC CARDS */}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "16px"
                }}
            >

                {/* AVG RATING */}

                <div
                    style={{
                        background: "#fff",
                        borderRadius: "20px",
                        padding: "22px",
                        boxShadow:
                            "0 4px 18px rgba(0,0,0,0.06)",
                        border:
                            "1px solid rgba(0,0,0,0.05)"
                    }}
                >

                    <p
                        style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#6b7280",
                            fontWeight: 500
                        }}
                    >
                        Average Rating
                    </p>

                    <h2
                        style={{
                            margin: "10px 0 6px",
                            fontSize: "38px",
                            fontWeight: 700,
                            color: "#111827"
                        }}
                    >
                        {avgRating.toFixed(1)}
                    </h2>

                    <StarDisplay rating={avgRating} />

                    <div
                        style={{
                            marginTop: "10px",
                            color:
                                ratingGrowth >= 0
                                    ? "#16a34a"
                                    : "#dc2626",
                            fontWeight: 600,
                            fontSize: "14px"
                        }}
                    >
                        {ratingGrowth >= 0 ? "↗" : "↘"}{" "}
                        {Math.abs(ratingGrowth)}%
                    </div>

                </div>

                {/* TOTAL RATINGS */}

                <div
                    style={{
                        background: "#fff",
                        borderRadius: "20px",
                        padding: "22px",
                        boxShadow:
                            "0 4px 18px rgba(0,0,0,0.06)",
                        border:
                            "1px solid rgba(0,0,0,0.05)"
                    }}
                >

                    <p
                        style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#6b7280",
                            fontWeight: 500
                        }}
                    >
                        Total Ratings
                    </p>

                    <h2
                        style={{
                            margin: "10px 0 0",
                            fontSize: "38px",
                            fontWeight: 700,
                            color: "#111827"
                        }}
                    >
                        {totalRatings}
                    </h2>

                </div>

                {/* DISTRIBUTION */}

                <div
                    style={{
                        background: "#fff",
                        borderRadius: "20px",
                        padding: "22px",
                        boxShadow:
                            "0 4px 18px rgba(0,0,0,0.06)",
                        border:
                            "1px solid rgba(0,0,0,0.05)"
                    }}
                >

                    <p
                        style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#6b7280",
                            fontWeight: 500
                        }}
                    >
                        Total Distribution
                    </p>

                    <h2
                        style={{
                            margin: "10px 0 0",
                            fontSize: "38px",
                            fontWeight: 700,
                            color: "#111827"
                        }}
                    >
                        {totalRatingsCount}
                    </h2>

                </div>

            </div>

            {/* RATING DISTRIBUTION */}

            <div
                style={{
                    background: "#fff",
                    borderRadius: "22px",
                    padding: "24px",
                    boxShadow:
                        "0 4px 18px rgba(0,0,0,0.06)"
                }}
            >

                <h3
                    style={{
                        marginBottom: "22px",
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#111827"
                    }}
                >
                    Rating Distribution
                </h3>

                {distributionData.map((item, idx) => {

                    const percentage =
                        totalRatingsCount > 0
                            ? (
                                item.count /
                                totalRatingsCount
                            ) * 100
                            : 0;

                    return (

                        <div
                            key={idx}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "14px",
                                marginBottom: "16px"
                            }}
                        >

                            <div
                                style={{
                                    width: "50px",
                                    fontWeight: 600,
                                    color: "#374151"
                                }}
                            >
                                {item.star} ★
                            </div>

                            <div
                                style={{
                                    flex: 1,
                                    height: "12px",
                                    background: "#e5e7eb",
                                    borderRadius: "999px",
                                    overflow: "hidden"
                                }}
                            >

                                <div
                                    style={{
                                        width: `${percentage}%`,
                                        height: "100%",
                                        borderRadius: "999px",
                                        background:
                                            item.star >= 4
                                                ? "#22c55e"
                                                : item.star === 3
                                                    ? "#f59e0b"
                                                    : "#ef4444",
                                        transition:
                                            "width 0.4s ease"
                                    }}
                                />

                            </div>

                            <div
                                style={{
                                    width: "40px",
                                    textAlign: "right",
                                    fontWeight: 600,
                                    color: "#374151"
                                }}
                            >
                                {item.count}
                            </div>

                        </div>
                    );
                })}

            </div>

            {/* CATEGORY RATINGS */}

            <div
                style={{
                    background: "#fff",
                    borderRadius: "22px",
                    padding: "24px",
                    boxShadow:
                        "0 4px 18px rgba(0,0,0,0.06)"
                }}
            >

                <h3
                    style={{
                        marginBottom: "22px",
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "#111827"
                    }}
                >
                    Category Ratings
                </h3>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "18px"
                    }}
                >

                    {categoryItems.map((cat) => {

                        const catData =
                            categoryRatings[cat.key] || {};

                        const avgCatRating =
                            parseFloat(
                                catData.averageRating || 0
                            );

                        const total =
                            catData.totalRatings || 0;

                        return (

                            <div
                                key={cat.key}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "16px"
                                }}
                            >

                                <div
                                    style={{
                                        width: "170px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        fontWeight: 500,
                                        color: "#374151"
                                    }}
                                >

                                    {getCategoryIcon(cat.key)}

                                    {cat.label}

                                </div>

                                <div
                                    style={{
                                        flex: 1,
                                        height: "10px",
                                        background: "#e5e7eb",
                                        borderRadius: "999px",
                                        overflow: "hidden"
                                    }}
                                >

                                    <div
                                        style={{
                                            width:
                                                `${(avgCatRating / 5) * 100}%`,
                                            height: "100%",
                                            borderRadius: "999px",
                                            background: "#f97316"
                                        }}
                                    />

                                </div>

                                <div
                                    style={{
                                        width: "90px",
                                        textAlign: "right",
                                        fontWeight: 600,
                                        color: "#111827"
                                    }}
                                >
                                    {avgCatRating.toFixed(1)} / 5
                                </div>

                                <div
                                    style={{
                                        width: "50px",
                                        textAlign: "right",
                                        color: "#9ca3af",
                                        fontSize: "13px"
                                    }}
                                >
                                    ({total})
                                </div>

                            </div>
                        );
                    })}

                </div>

            </div>

        </div>
    );
}

export default FoodRatingsTab;